import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import IndiaMap from "@/components/IndiaMap";
import { ArrowUpRight } from "lucide-react";

export default function MapPage() {
  const [data, setData] = useState({ total_hotels: 0, states: [], operational_hotels: 0, upcoming_hotels: 0, total_states: 0 });
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.summary(), api.listHotels({ limit: 2000 })])
      .then(([sum, hs]) => {
        setData(sum);
        setHotels(hs);
      })
      .finally(() => setLoading(false));
  }, []);

  const statsByState = Object.fromEntries(data.states.map((s) => [s.state, s]));
  const activeStatesList = data.states.filter((s) => s.hotel_count > 0);
  const hotelsWithGeo = hotels.filter((h) => typeof h.latitude === "number" && typeof h.longitude === "number");
  const upcomingWithGeo = hotelsWithGeo.filter((h) => h.status === "upcoming").length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14">
      <div className="mb-6 sm:mb-10">
        <div className="font-label text-xs text-[var(--brand-blue)] mb-3">Interactive Map</div>
        <h1 data-testid="map-page-title" className="font-display text-4xl sm:text-6xl leading-none mb-4">
          India, at a glance.
        </h1>
        <p className="text-[var(--ink-soft)] max-w-2xl text-sm sm:text-base">
          Each dot is one city. <span className="text-[var(--brand-blue)] font-semibold">Blue</span> = operational,{" "}
          <span className="text-[var(--brand-orange)] font-semibold">orange</span> = upcoming, split-colour = mixed.
          The number inside a dot shows how many hotels are in that city — tap it to pick the exact hotel.
        </p>
        {hotels.length > 0 && (
          <div data-testid="map-geo-summary" className="mt-4 flex items-center gap-3 flex-wrap text-xs text-[var(--ink-soft)]">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[var(--brand-blue)]" />
              {hotelsWithGeo.length - upcomingWithGeo} operational on map
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[var(--brand-orange)]" />
              {upcomingWithGeo} upcoming on map
            </span>
            {hotels.length - hotelsWithGeo.length > 0 && (
              <span className="text-[var(--brand-orange)]">
                · {hotels.length - hotelsWithGeo.length} hotel{hotels.length - hotelsWithGeo.length > 1 ? "s" : ""} missing lat/lng
              </span>
            )}
          </div>
        )}
      </div>

      {/* Map + Stats side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map column */}
        <div className="lg:col-span-2 order-2 lg:order-1">
          {loading ? (
            <div className="text-[var(--ink-soft)] font-label text-sm">Loading map…</div>
          ) : (
            <IndiaMap activeStates={statsByState} hotels={hotels} height={640} />
          )}
        </div>

        {/* Prominent stats column */}
        <aside data-testid="map-stats-panel" className="lg:col-span-1 order-1 lg:order-2 flex flex-col gap-3 sm:gap-4">
          <BigStat
            testId="map-stat-hotels"
            label="Hotels"
            value={data.total_hotels}
            hint="Total properties across India"
            bg="bg-[var(--ink)]"
            fg="text-white"
            accent="text-[var(--brand-yellow)]"
          />
          <BigStat
            testId="map-stat-operational"
            label="Operational"
            value={data.operational_hotels}
            hint="Live and welcoming guests"
            bg="bg-[var(--brand-blue)]"
            fg="text-white"
            accent="text-white/70"
          />
          <BigStat
            testId="map-stat-upcoming"
            label="Upcoming"
            value={data.upcoming_hotels}
            hint="Opening soon"
            bg="bg-[var(--brand-orange)]"
            fg="text-white"
            accent="text-white/70"
          />
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <MiniStat testId="map-stat-cities" label="Cities" value={data.total_cities} />
            <MiniStat testId="map-stat-states" label="States" value={data.total_states} />
          </div>
        </aside>
      </div>

      {/* Active states list */}
      <div className="mt-14">
        <div className="font-label text-xs text-[var(--ink-soft)] mb-4">
          Active States ({activeStatesList.length})
        </div>
        {activeStatesList.length === 0 ? (
          <div className="text-sm text-[var(--ink-soft)]">No active states yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {activeStatesList.map((s) => (
              <Link
                key={s.state}
                to={`/state/${encodeURIComponent(s.state)}`}
                data-testid={`map-active-${slug(s.state)}`}
                className="group flex items-center justify-between border border-[var(--ink)] px-4 py-3 hover:bg-[var(--paper-2)]"
              >
                <div>
                  <div className="font-display text-lg">{s.state}</div>
                  <div className="text-xs text-[var(--ink-soft)] mt-1">
                    <span className="inline-flex items-center gap-1 mr-3">
                      <span className="font-display text-base text-[var(--ink)]">{s.hotel_count}</span> hotels
                    </span>
                    <span className="inline-flex items-center gap-1 mr-3">
                      <span className="font-display text-base text-[var(--brand-blue)]">{s.rooms}</span> rooms
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="font-display text-base text-[var(--ink)]">{s.city_count}</span> {s.city_count === 1 ? "city" : "cities"}
                    </span>
                    {s.upcoming > 0 && (
                      <div className="mt-1 text-[var(--brand-orange)]">+ {s.upcoming} upcoming</div>
                    )}
                  </div>
                </div>
                <ArrowUpRight className="w-4 h-4 group-hover:text-[var(--brand-blue)]" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BigStat({ label, value, hint, bg, fg, accent, testId }) {
  return (
    <div data-testid={testId} className={`${bg} ${fg} p-5 sm:p-6`}>
      <div className="flex items-baseline justify-between gap-3">
        <div className="font-label text-xs opacity-90">{label}</div>
        <div className={`font-label text-[10px] ${accent}`}>Live</div>
      </div>
      <div className="font-display text-5xl sm:text-6xl leading-none mt-3">{value}</div>
      <div className="text-xs mt-3 opacity-80">{hint}</div>
    </div>
  );
}

function MiniStat({ label, value, testId }) {
  return (
    <div data-testid={testId} className="border border-[var(--ink)] p-4">
      <div className="font-label text-[10px] text-[var(--ink-soft)]">{label}</div>
      <div className="font-display text-3xl leading-none mt-1">{value}</div>
    </div>
  );
}

function slug(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
